/**
 * <aya-treatment-index> — filtering + "show more" for the collection-driven
 * treatment index.
 *
 * All taxonomy comes from the markup, which Liquid generated from controlled
 * Collection references. This file contains no treatment names and no
 * hardcoded category or concern list, so adding a Product to a Collection is
 * the only step needed to extend the filters.
 *
 * Facets are independent: ticking a category AND a concern narrows across both
 * (a card must satisfy every ACTIVE facet). Within one facet, `data-match-mode`
 * decides whether ticking two options means "either" (any) or "both" (all).
 */
class AyaTreatmentIndex extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.aya-treatment-index__card'));
    this.inputs = Array.from(this.querySelectorAll('input[type="checkbox"][data-facet]'));
    this.emptyEl = this.querySelector('[data-role="empty"]');
    this.clearEl = this.querySelector('[data-action="clear"]');
    this.mobileToggle = this.querySelector('.aya-treatment-index__mobile-toggle');

    if (!this.cards.length) return;

    this.perPage = parseInt(this.dataset.perPage, 10) || 0;
    this.matchMode = this.dataset.matchMode === 'all' ? 'all' : 'any';
    this.expanded = false;

    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);

    this.inputs.forEach((input) => input.addEventListener('change', this.onChange));
    this.clearEl?.addEventListener('click', this.onClear);

    // The filters sidebar is meant to sit open and static at desktop widths
    // and collapse into a tap-to-open <details> only on mobile — but modern
    // browsers render a closed <details>'s content as unpainted/un-hit-testable
    // regardless of any CSS `display` override on its children (a trick that
    // used to work and no longer does). The only reliable fix is to actually
    // set the real `open` property from JS. Liquid renders the sidebar `open`
    // by default so it's usable even if this script fails to load.
    if (this.mobileToggle) {
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.syncMobileToggle = (event) => {
        this.mobileToggle.open = event.matches;
      };
      this.syncMobileToggle(this.desktopQuery);
      this.desktopQuery.addEventListener('change', this.syncMobileToggle);
    }

    // Lets a page (e.g. /pages/lasers-and-lights) land pre-filtered to one
    // category via a section setting, without a separate template/section
    // per category. Same idea as the merchant ticking the box by hand.
    const defaultCategory = this.dataset.defaultCategory;
    if (defaultCategory) {
      const match = this.inputs.find(
        (input) => input.dataset.facet === 'categories' && input.value === defaultCategory
      );
      if (match) match.checked = true;
    }

    // Same idea as defaultCategory above, but pre-checks a Treatable concern
    // instead - lets a page (e.g. /pages/acne) land pre-filtered to one
    // concern via a section setting.
    const defaultConcern = this.dataset.defaultConcern;
    if (defaultConcern) {
      const match = this.inputs.find(
        (input) => input.dataset.facet === 'concerns' && input.value === defaultConcern
      );
      if (match) match.checked = true;
    }

    this.buildShowMore();
    this.apply();
  }

  disconnectedCallback() {
    this.inputs?.forEach((input) => input.removeEventListener('change', this.onChange));
    this.clearEl?.removeEventListener('click', this.onClear);
    this.moreButton?.removeEventListener('click', this.onShowMore);
    this.desktopQuery?.removeEventListener('change', this.syncMobileToggle);
  }

  /** Tokens for one facet on one card, e.g. ["injectables", "lasers-and-lights"]. */
  static tokens(card, facet) {
    const raw = facet === 'concerns' ? card.dataset.concerns : card.dataset.categories;
    return (raw || '').split(/\s+/).filter(Boolean);
  }

  /** Ticked values, grouped by facet. Facets with nothing ticked are omitted. */
  activeFacets() {
    const active = {};
    this.inputs.forEach((input) => {
      if (!input.checked) return;
      const facet = input.dataset.facet;
      (active[facet] = active[facet] || []).push(input.value);
    });
    return active;
  }

  matches(card, active) {
    return Object.entries(active).every(([facet, values]) => {
      const tokens = AyaTreatmentIndex.tokens(card, facet);
      return this.matchMode === 'all'
        ? values.every((v) => tokens.includes(v))
        : values.some((v) => tokens.includes(v));
    });
  }

  buildShowMore() {
    if (!this.perPage || this.cards.length <= this.perPage) return;

    this.moreButton = document.createElement('button');
    this.moreButton.type = 'button';
    this.moreButton.className = 'button-secondary aya-treatment-index__more';
    this.moreButton.textContent = 'Show all treatments';
    this.onShowMore = () => {
      this.expanded = true;
      this.moreButton.remove();
      this.apply();
    };
    this.moreButton.addEventListener('click', this.onShowMore);
    this.querySelector('.aya-treatment-index__cards')?.after(this.moreButton);
  }

  apply() {
    const active = this.activeFacets();
    const filtering = Object.keys(active).length > 0;
    let shown = 0;

    this.cards.forEach((card) => {
      const eligible = this.matches(card, active);
      // While filtering, never truncate — the merchant asked to narrow, not paginate.
      const withinPage =
        filtering || this.expanded || !this.perPage ? true : shown < this.perPage;
      const visible = eligible && withinPage;

      card.hidden = !visible;
      card.setAttribute('aria-hidden', visible ? 'false' : 'true');
      // Keep hidden cards out of the tab order.
      if (visible) {
        card.removeAttribute('tabindex');
      } else {
        card.setAttribute('tabindex', '-1');
      }
      if (eligible) shown += 1;
    });

    if (this.emptyEl) this.emptyEl.hidden = shown > 0;
    // Reset Filters stays visible at all times on ayaskin.com's own treatments
    // page, even with nothing ticked - it's a permanent sidebar action, not a
    // "filtering is active" indicator.
    if (this.moreButton) this.moreButton.hidden = filtering || this.expanded;
  }

  onChange() {
    this.apply();
  }

  onClear() {
    this.inputs.forEach((input) => {
      input.checked = false;
    });
    this.apply();
    this.inputs[0]?.focus();
  }
}

if (!customElements.get('aya-treatment-index')) {
  customElements.define('aya-treatment-index', AyaTreatmentIndex);
}
